/**
 * Configuration et données pour le composant Services
 */
import { themeColors, colors } from '../../utils/colors';

// Interface pour le service
export interface ServiceDetail {
  id: string;
  title: string;
  description: string;
  features: string[];
  featureColor: string;
  iaValues: string[];
  iaTitle?: string;
  icon: string;
}

// Extension de l'interface pour les fonctionnalités supplémentaires
export interface ServiceDetailExtended extends ServiceDetail {
  markerColor?: string;
  iconPath?: string;
  iconColor?: string;
}

// Données des services
export const services: ServiceDetailExtended[] = [
  {
    id: 'accompagnement-ia',
    title: 'Accompagnement IA',
    description: "Diagnostic, cas d’usage, choix d’outils et déploiement. <br> Un accompagnement IA complet, responsable et sécurisé.",
    features: [
      'Diagnostic IA et cartographie des opportunités',
      'Ateliers cas d’usage par métier',
      'Sélection / POC des outils (Copilots, RAG, agents)',
      'Déploiement, intégration et sécurité (RGPD)',
      'Formation, adoption et gouvernance IA responsable'
    ],
    featureColor: themeColors['won-pink'],
    iaValues: [
      "ROI mesuré et gains de productivité",
      "Automatisations fiables et auditables",
      "Gouvernance, sécurité et conformité (RGPD)"
    ],
    // Icône "puce IA" détaillée (groupe SVG multi-éléments pour un rendu net)
    icon: '<g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke">\n            <rect x="8" y="8" width="8" height="8" rx="2"/>\n            <path d="M12 2v4M12 18v4M2 12h4M18 12h4M6 6l2 2M18 6l-2 2M6 18l2-2M18 18l-2-2"/>\n            <path d="M10 10h4v4h-4z"/>\n          </g>'
  },
  {
    id: 'strategie',
    title: 'Stratégie Numérique',
    description: "Nous élaborons avec vous une feuille de route digitale personnalisée pour accélérer votre transformation.",
    features: [
      "Audit numérique complet",
      "Élaboration de feuille de route sur 12-24 mois",
      "Accompagnement à la transformation",
      "Formation du comité de direction",
    ],
    featureColor: themeColors['won-blue'],
    iaValues: [
      "Algorithmes prédictifs pour évaluer le ROI potentiel",
      "Dashboard intelligent avec recommandations automatisées",
      "Analyse prédictive des tendances sectorielles"
    ],
    icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2'
  },
  {
    id: 'facturation',
    title: 'Facture Électronique',
    description: "Anticipez la réglementation 2024-2026 avec une solution de dématérialisation complète et adaptée à vos besoins.",
    features: [
      "Diagnostic de conformité réglementaire",
      "Choix de solution adaptée (PDP plateforme de dématérialisation partenaire ou PPF portail public de facturation)",
      "Migration des processus existants",
      "Intégration aux SI existants",
      "Formation des équipes comptables",
      "Conformité et archivage légal"
    ],
    featureColor: themeColors['won-teal'],
    iaValues: [
      "Reconnaissance et classement automatique des factures",
      "Détection des anomalies et contrôle qualité",
      "Prédiction des flux de trésorerie"
    ],
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
  },
  {
    id: 'rgpd',
    title: 'RGPD',
    description: "Assurez la conformité RGPD avec une approche proactive et systématique de la protection des données.",
    features: [
      "Audit de conformité et cartographie des données",
      "Mise en place des registres obligatoires",
      "Révision des mentions légales et CGU",
      "Procédures de gestion des droits",
      "Formation à la protection des données",
      "DPO externalisé"
    ],
    featureColor: themeColors['won-emerald'],
    iaValues: [
      "Identification intelligente des données sensibles",
      "Automatisation des procédures de demandes d'accès",
      "Monitoring en temps réel des risques de non-conformité"
    ],
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
  },
  {
    id: 'cybersecurite',
    title: 'Diagnostic Cybersécurité',
    description: "Protégez votre entreprise contre les menaces numériques avec notre audit complet et nos solutions de sécurité sur mesure.",
    features: [
      "Évaluation des risques humains",
      "Revue des processus de sécurité",
      "Plan de remédiation priorisé",
      "Formation à la cybersécurité",
      "Mise en place de solutions de protection"
    ],
    featureColor: themeColors['won-orange'],
    iaValues: [
      "Détection avancée des comportements anormaux",
      "Simulation d'attaques intelligentes",
      "Monitoring prédictif des menaces"
    ],
    icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
  },
  // {
  //   id: 'donnees',
  //   title: 'Analyse de Données',
  //   description: "Exploitez pleinement le potentiel de vos données avec nos solutions d'analyse avancée et tableaux de bord décisionnels.",
  //   features: [
  //     "Audit de la qualité des données",
  //     "Mise en place d'entrepôts de données",
  //     "Tableaux de bord décisionnels",
  //     "Automatisation des rapports",
  //     "Gouvernance des données"
  //   ],
  //   featureColor: themeColors['won-blue-dark'],
  //   iaValues: [
  //     "Analyse prédictive des tendances futures",
  //     "Détection automatique des anomalies",
  //     "Recommandations d'actions basées sur l'historique"
  //   ],
  //   icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
  // },
  {
    id: 'processus',
    title: 'Digitalisation des Processus',
    description: "Transformez vos workflows avec l'automatisation intelligente et la digitalisation complète de vos processus métier.",
    features: [
      "Cartographie des processus actuels",

      "Sélection d'outils adaptés",
      "Développement sur mesure",
      "Automatisation des tâches répétitives",
      "Intégration et déploiement progressif"
    ],
    featureColor: themeColors['won-pink-light'],
    iaValues: [
      "Automatisation intelligente avec adaptation dynamique",
      "Optimisation continue basée sur l'analyse de performance"
    ],
    icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z'
  },
];

// Textes pour les titres et descriptions
export const textContent = {
  sectionTitle: "Nos Services",
  sectionSubtitle: "Des solutions sur mesure pour accélérer votre transformation numérique et maximiser votre impact digital.",
  badgeText: "Expertise digitale",
  ctaTitle: "Prêt à réinventer votre futur digital ?",
  ctaDescription: "Parlons de votre projet et explorons comment nos services peuvent propulser votre entreprise.",
  ctaButtonText: "Contactez-nous",
};

// Configuration de l'animation
export const animationConfig = {
  cardAnimationDelay: 100, // délai en ms entre l'animation de chaque carte
  modalTransitionSpeed: 300 // vitesse de transition du modal en ms
};
